<template>
	<div class="import-shop-products">
		<b-loading :is-full-page="true" :active.sync="isLoading" :can-cancel="false"></b-loading>
		<h1>Import Chemicals</h1>

		<p class="description">
			This will import chemicals data. Please refer to the provided CSV template.<br /><br />Headers must be in lowercase and match the required
			fields: <code>name</code>, <code>code</code>, and <code>parent</code>. The <code>parent</code> field should match an existing chemical name or
			be left empty.
		</p>

		<b-field class="file">
			<b-upload v-model="file" ref="file" accept=".csv,text/csv" native>
				<a class="button is-primary">
					<b-icon icon="upload"></b-icon>
					<span>Select a file</span>
				</a>
			</b-upload>
			<span class="file-name" v-if="file">
				{{ file.name }}
			</span>
		</b-field>

		<!-- Progress bar for validation -->
		<div v-if="validationProgress > 0">
			<br />
			<p>{{ validationStatus }}</p>
			<b-progress :value="Math.ceil(validationProgress)" show-value format="percent" type="is-info" size="is-medium">
				{{ currentValidationRow }} of {{ totalRows }}
			</b-progress>
		</div>

		<!-- Progress bar for upload -->
		<div v-if="uploadProgress > 0">
			<br />
			<p>{{ uploadStatus }}</p>
			<b-progress :value="Math.ceil(uploadProgress)" show-value format="percent" type="is-success" size="is-medium">
				{{ currentUploadRow }} of {{ totalRows }}
			</b-progress>
		</div>

		<div class="button-container">
			<div class="big-button" :class="{ 'is-disabled': !fileValid }" @click="fileValid && uploadChemicals()">
				Upload CSV File
			</div>
		</div>

		<!-- Error Table -->
		<div v-if="validationErrors.length > 0" class="error-section">
			<h3 class="error-title">Validation Errors</h3>
			<b-table
				:data="validationErrors"
				:columns="errorColumns"
				:paginated="validationErrors.length > 10"
				:per-page="10"
				:pagination-simple="false"
				:pagination-position="'bottom'"
				class="error-table"
			>
			</b-table>
		</div>
	</div>
</template>

<script>
export default {
	name: 'ImportChemicals',
	data: () => ({
		isLoading: false,
		file: null,
		fileValid: false,
		// Progress tracking for validation
		validationProgress: 0,
		validationStatus: '',
		currentValidationRow: 0,
		totalRows: 0,
		// Progress tracking for upload
		uploadProgress: 0,
		uploadStatus: '',
		currentUploadRow: 0,
		// Parsed CSV data
		csvData: [],
		parentIds: {}, // Store parent IDs for quick lookup
		// Error tracking
		validationErrors: [],
		errorColumns: [
			{
				field: 'row',
				label: 'Row',
				width: '80',
				numeric: true
			},
			{
				field: 'type',
				label: 'Error Type',
				width: '150'
			},
			{
				field: 'field',
				label: 'Field',
				width: '100'
			},
			{
				field: 'value',
				label: 'Value',
				width: '200'
			},
			{
				field: 'message',
				label: 'Error Message'
			}
		]
	}),
	watch: {
		file(newFile) {
			// Reset validation state when new file is selected
			this.fileValid = false
			this.validationProgress = 0
			this.validationStatus = ''
			this.currentValidationRow = 0
			this.totalRows = 0
			this.csvData = []
			this.parentIds = {}
			this.validationErrors = []

			if (newFile) {
				this.readCsvFile(newFile)
			}
		}
	},
	methods: {
		/**
		 * Read and validate CSV file
		 */
		readCsvFile(file) {
			return new Promise((resolve, reject) => {
				const reader = new FileReader()
				reader.onload = async e => {
					const text = e.target.result

					// Parse CSV into lines
					const lines = text.split(/\r?\n/).filter(line => line.trim())
					if (lines.length < 2) {
						this.validationErrors.push({
							row: 0,
							type: 'File Error',
							field: 'File',
							value: file.name,
							message: 'CSV file is empty or has no data rows'
						})
						return reject(new Error('Empty CSV'))
					}

					// Check headers
					const headerLine = lines[0] || ''
					const headers = headerLine.split(',').map(h => h.trim().toLowerCase())
					const required = ['name', 'code', 'parent']
					const missing = required.filter(h => !headers.includes(h))

					if (missing.length > 0) {
						missing.forEach(col => {
							this.validationErrors.push({
								row: 1,
								type: 'Missing Column',
								field: col,
								value: '-',
								message: `Required column "${col}" is missing from header`
							})
						})
						return reject(new Error('Missing required columns'))
					}

					// Parse data rows
					const nameIdx = headers.indexOf('name')
					const codeIdx = headers.indexOf('code')
					const parentIdx = headers.indexOf('parent')
					this.totalRows = lines.length - 1 // Exclude header
					this.validationStatus = 'Validating CSV data...'

					// Parse all rows first
					const rowData = []
					for (let i = 1; i < lines.length; i++) {
						const row = lines[i].trim()
						if (!row) continue

						const cols = row.split(',').map(col => col.trim())
						const name = (cols[nameIdx] || '').trim()
						const code = (cols[codeIdx] || '').trim()
						const parent = (cols[parentIdx] || '').trim()

						// Check required fields
						if (!name) {
							this.validationErrors.push({
								row: i,
								type: 'Missing Field',
								field: 'name',
								value: '',
								message: 'Name is required'
							})
							return reject(new Error('Missing name'))
						}
						if (!code) {
							this.validationErrors.push({
								row: i,
								type: 'Missing Field',
								field: 'code',
								value: '',
								message: 'Code is required'
							})
							return reject(new Error('Missing code'))
						}

						rowData.push({ name, code, parent, rowNumber: i })
					}

					// Validate each row with API
					let hasErrors = false
					for (let i = 0; i < rowData.length; i++) {
						const { name, code, parent, rowNumber } = rowData[i]
						this.currentValidationRow = i + 1
						this.validationProgress = ((i + 1) / rowData.length) * 100

						try {
							// Check if name already exists
							const nameResult = await this.$api.getChemicals({ data: { exact_name: name } })
							if (nameResult && nameResult.data && nameResult.data.count > 0) {
								this.validationErrors.push({
									row: rowNumber,
									type: 'Duplicate',
									field: 'name',
									value: name,
									message: 'Chemical name already exists in database'
								})
								hasErrors = true
							}

							// Check if code already exists
							const codeResult = await this.$api.getChemicals({ data: { code } })
							if (codeResult && codeResult.data && codeResult.data.count > 0) {
								this.validationErrors.push({
									row: rowNumber,
									type: 'Duplicate',
									field: 'code',
									value: code,
									message: 'Chemical code already exists in database'
								})
								hasErrors = true
							}

							// Check parent if provided
							let parentId = null
							if (parent) {
								const parentResult = await this.$api.getChemicals({ data: { exact_name: parent } })
								if (!parentResult || !parentResult.data || parentResult.data.count < 1) {
									this.validationErrors.push({
										row: rowNumber,
										type: 'Invalid Parent',
										field: 'parent',
										value: parent,
										message: 'Parent chemical does not exist in database'
									})
									hasErrors = true
								} else if (parentResult.data.count > 1) {
									this.validationErrors.push({
										row: rowNumber,
										type: 'Ambiguous Parent',
										field: 'parent',
										value: parent,
										message: 'Multiple parent chemicals found with this name'
									})
									hasErrors = true
								} else {
									// Store parent ID for later use
									parentId = parentResult.data.list[0]._id
									this.parentIds[parent] = parentId
								}
							}

							// Store validated data
							this.csvData.push({ name, code, parent, parentId, rowNumber })
						} catch (err) {
							console.error('Validation error:', err)
							this.validationErrors.push({
								row: rowNumber,
								type: 'API Error',
								field: 'API',
								value: '-',
								message: 'Error during validation - please try again'
							})
							hasErrors = true
						}
					}

					// Reset progress
					this.validationProgress = 0
					this.validationStatus = ''
					this.currentValidationRow = 0

					if (hasErrors) {
						this.fileValid = false
						this.$buefy.toast.open({
							message: `Found ${this.validationErrors.length} validation error(s). Please check the table below.`,
							type: 'is-warning'
						})
					} else {
						this.fileValid = true
						this.$buefy.toast.open({
							message: 'CSV validation completed successfully!',
							type: 'is-success'
						})
					}

					resolve(text)
				}
				reader.onerror = err => reject(err)
				reader.readAsText(file)
			})
		},

		/**
		 * Upload all validated chemicals
		 */
		async uploadChemicals() {
			if (!this.fileValid || this.csvData.length === 0) return

			this.isLoading = true
			this.uploadProgress = 0
			this.uploadStatus = 'Uploading chemicals...'
			this.currentUploadRow = 0

			let successCount = 0
			let errorCount = 0

			for (let i = 0; i < this.csvData.length; i++) {
				const { name, code, parentId } = this.csvData[i]
				this.currentUploadRow = i + 1
				this.uploadProgress = ((i + 1) / this.csvData.length) * 100

				try {
					const data = {
						name: name ? name.trim() : '',
						code: code ? code.trim() : '',
						parent: parentId || null // Use parent ID if available
					}

					const result = await this.$api.addChemical({ data })
					if (result) {
						successCount++
					}
				} catch (err) {
					console.error('Upload error:', err)
					errorCount++
					this.$buefy.toast.open({
						message: `Failed to upload chemical "${name}"`,
						type: 'is-danger'
					})
				}
			}

			// Reset progress
			this.uploadProgress = 0
			this.uploadStatus = ''
			this.currentUploadRow = 0
			this.isLoading = false

			// Show summary
			if (errorCount === 0) {
				this.$buefy.toast.open({
					message: `Successfully uploaded ${successCount} chemicals!`,
					type: 'is-success',
					duration: 5000
				})
			} else {
				this.$buefy.toast.open({
					message: `Upload completed: ${successCount} success, ${errorCount} failed`,
					type: 'is-warning',
					duration: 5000
				})
			}

			// Reset form
			this.file = null
			this.fileValid = false
			this.csvData = []
			this.parentIds = {}
			this.validationErrors = []
		}
	}
}
</script>

<style scoped>
@import '../styles/GeneralStyles.css';

.is-disabled {
	opacity: 0.6;
	cursor: not-allowed !important;
	pointer-events: none;
}

.error-section {
	margin-top: 20px;
	padding: 15px;
	background-color: #fef5f5;
	border: 1px solid #f56565;
	border-radius: 6px;
}

.error-title {
	color: #e53e3e;
	font-size: 18px;
	font-weight: 600;
	margin-bottom: 15px;
}

.error-table {
	background-color: white;
	border-radius: 4px;
}
</style>
